import { useDispatch, useSelector } from "react-redux"
import { useScope } from "../../../container/SelectProjectCompanyContainer"
import Send from "../../communication"
import { setData } from "../../redux/app/Actions"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { timestampToHumanReadable } from "../timestampToHumanReadable"
import moment from "moment"
import { useCallback, useEffect, useState } from "react"
import { customToast } from "../../../components/Toast"

export const useUsers = (projectId = null) => {
  const { scope, scopeType, projectsByScope } = useScope();
  const dispatch = useDispatch()
  const queryClient = useQueryClient();

  const { users, tags, tagsProject } = useSelector((state) => state.app) 

  const newScope = {
    id: projectId ? projectId : scope?.id,
    type: projectId ? 'project': scopeType
  };

  const loadDataUsers = async () => {
    const newScope = {
      id: projectId ? projectId : scope?.id,
      type: projectId ? 'project': scopeType
    };

    
    const data = await Send.get(`/projects/users/${newScope?.id}`, `scopeType=${newScope?.type}`)

    const sortedData = data.sort((a, b) => {
      return `${a.user.name}`.localeCompare(b.user.name);
    });
    
    return sortedData;
  }

  const loadDataTags = async () => {
    const tagsResponse = await Send.get(`/tags`)
    const tagsProjectResponse = Array.isArray(tagsResponse) &&  tagsResponse.length > 0 && tagsResponse?.filter(
      (tag) => !tag?.projectId || tag?.projectId === "" || projectsByScope.some(proj => proj?.id === tag?.projectId) || tag?.projectId === scope?.id,
    )
    dispatch(setData({ tags: tagsResponse, tagsProject: tagsProjectResponse }))
    return tagsResponse
  }

  const loadDataUserTags = async () => {

    const response = await Send.get(`/projects/users/tags/${newScope?.id}`, `scopeType=${newScope?.type}`)

    const userArray = Array.isArray(response) && response.map((user) => {
      const analyticsData = queryUsersResponse?.data || [];
      const analyticsItem = analyticsData.find((item) => item?.user?.id === user?.id);

      const parsedConfig = user?.projectConfig ? JSON.parse(user?.projectConfig) : null
      const projectUserHasEsm =  user?.projectConfig === null || parsedConfig?.hasEsm === true

      delete user.projectConfig

      if (analyticsItem) {
        const activity = analyticsItem.user.analytics?.event_timestamp && moment(new Date(analyticsItem.user.analytics?.event_timestamp / 1000)).locale('pt-br').fromNow() || 0
        return {
          ...user,
          ...analyticsItem?.user,
          projectUserHasEsm,
          tag_user: user?.tag_user?.filter((item) => item.id !== null),
          analytics: analyticsItem.user.analytics,
          lastAcitvity: {
            status: activity !== 0 ? "Ativo" : "Inativo",
            activity: activity
          }
        };
      }
    
      return {
        ...user,
        projectUserHasEsm,
      };
    });
    
    dispatch(setData({ users: userArray }));
    
    return userArray;
  }

  const hasNotSelected = false

  const refresh = { refetchOnWindowFocus: false, staleTime: Infinity, refetchInterval: Infinity, cacheTime: 0 }
  
  const queryUsers = ["users", newScope?.id];
  const queryUsersResponse = useQuery(queryUsers, loadDataUsers, refresh);

  const queryTags = ["tags", newScope?.id];
  const queryTagsResponse = useQuery(queryTags, loadDataTags, refresh);

  const queryUserTags = ["user-list-tags", newScope?.id, queryUsersResponse?.isFetched];
  const queryUserTagsResponse = useQuery(queryUserTags, loadDataUserTags, refresh);

  const reload = useCallback(async () => {
    await queryUsersResponse.refetch();
    await queryTagsResponse.refetch();
    await queryUserTagsResponse.refetch();
  }, [queryUsersResponse, queryTagsResponse, queryUserTagsResponse]);

  const timestamps = [queryTagsResponse?.dataUpdatedAt, queryUserTagsResponse?.dataUpdatedAt, queryUsersResponse?.dataUpdatedAt];
  const dateTimeStamp = timestamps.reduce((prev, current) => {
    if (current > prev) {
      return current;
    }
    return prev;
  }, 0);

  const lastUpdated = timestampToHumanReadable(dateTimeStamp);

  const isLoading = [queryTagsResponse, queryUserTagsResponse, queryUsersResponse].some((query) => query.isLoading)
  const isInitialLoading = [queryTagsResponse, queryUserTagsResponse, queryUsersResponse].some((query) => query.isInitialLoading)
  const isFetching = [queryTagsResponse, queryUserTagsResponse, queryUsersResponse].some((query) => query.isFetching)
  const isRefetching = [queryTagsResponse, queryUserTagsResponse, queryUsersResponse].some((query) => query.isRefetching)
  const isFetched = [queryTagsResponse, queryUserTagsResponse, queryUsersResponse].some((query) => query.isFetched)

  return {
    users,
    tags,
    tagsProject,
    isLoading,
    isInitialLoading,
    isFetching,
    isRefetching,
    isFetched,
    lastUpdated,
    reload,
    refresh: hasNotSelected,
  }
}

export const useUsersProject = (refetching = false) => {
  const dispatch = useDispatch()
  const { scope, scopeType } = useScope()

  const { usersProject } = useSelector((state) => state.app)

  const loadDataUsersProjects = async () => {
    const usersResponse = await Send.get(`/users/project/${scope?.id}`, `scopeType=${scopeType}`)

    dispatch(setData({ usersProject: usersResponse }))
    return usersResponse
  }

  const { data, isFetching, isFetched, isLoading, refetch, isError } = useQuery(
    ["user-projects", scope?.id],
    loadDataUsersProjects,
  )

  useEffect(() => {
    if(refetching){
      refetch()
    }
  }, [refetching])

  return {
    usersProject: usersProject || data,
    refetch,
    isFetching,
    isFetched,
    isLoading,
    isError,
  }
}

export const useProjectsByUser = (userId) => {
  const loadDataUsersProjects = async () => {
    const usersResponse = await Send.get(`/projects/users/user/${userId}`)
    return usersResponse
  }

  const { data, isFetching, isFetched, isLoading, refetch, isError } = useQuery(
    ["projects-user", userId],
    loadDataUsersProjects
  )

  return {
    projectsByUser: data,
    refetch,
    isFetching,
    isFetched,
    isLoading,
    isError,
  }
}

export const useUserWithDateFilter = (startDate, endDate, projectIds) => {
  const { scope, scopeType } = useScope();

  const loadDataUsers = async () => {    
    let extraQuery = `scopeType=${scopeType}`;
    
    if (startDate && endDate && projectIds) {
      extraQuery += `&startDate=${startDate}&endDate=${endDate}&projects=${projectIds}`;
    } else if (projectIds) {
      extraQuery = `projects=${projectIds}`;
    } else if (startDate && endDate) {
      extraQuery += `&startDate=${startDate}&endDate=${endDate}`;
    }

    const query = !projectIds ? `/projects/users/tags/${scope?.id}` : `/projects/users/tags`;
    
    const data = await Send.get(`${query}`, `${extraQuery}`);

    return data;
  }
  
  const queryUsers = ["users-date", scope?.id, projectIds, startDate, endDate];
  const { data, isLoading, isInitialLoading, isFetched, isFetching, isRefetching } = useQuery(queryUsers, loadDataUsers);

  return {
    users: data,
    isLoading,
    isInitialLoading,
    isFetching,
    isRefetching,
    isFetched,
  }
}

export const useTechnicianUsers = (projects, getAll = false) => {
  const [technicalList, setTechnicalList] = useState([]);
  const { scope, scopeType } = useScope();

  const loadAllTec = async (projects) => {

    const extraQuery = projects && !getAll ?  `projects=${projects}` : `scopeType=${scopeType}`
    const query = !projects && scope?.id && !getAll ? `/${scope?.id}` : ``
    try {
      const response = await Send.get(
        `/users/technicians${query}`, 
        `${extraQuery}`,
      );
      setTechnicalList(response.map((row) => ({ value: row?.id, label: row?.name, email: row?.email })));
    } catch (error) {
      console.log("Error => ", error);
      customToast("Erro ao carregar técnicos", "error");
    }
  };

  useEffect(() => {
    if(scope?.id || projects || getAll){
      loadAllTec(projects);
    }
  }, [projects, scope, getAll]);

  return technicalList;
};

export function useOnlineStatus() {
  const online = useSelector(state => state.app.online)
  return online
}